import React, { useState, useEffect } from "react";
import api from "@/services/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Loader2, Crown, Gift, TrendingUp } from "lucide-react";

const fmt = (n) => `$${(n || 0).toLocaleString("es-CO")}`;
const CANJE_BADGE = { solicitado: "bg-yellow-100 text-yellow-800", aprobado: "bg-blue-100 text-blue-800", entregado: "bg-green-100 text-green-800", rechazado: "bg-red-100 text-red-700" };

export default function MayoristaClub() {
  const [club, setClub] = useState(null);
  const [reportes, setReportes] = useState(null);
  const [canjeando, setCanjeando] = useState("");

  const load = () => {
    api.get("/b2b/me/club").then((r) => setClub(r.data)).catch(() => toast.error("Error cargando el Club"));
    api.get("/b2b/me/reportes").then((r) => setReportes(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const canjear = async (r) => {
    setCanjeando(r.id);
    try {
      await api.post("/b2b/me/club/canjear", { recompensa_id: r.id });
      toast.success(`Canje solicitado: ${r.nombre}. Administración lo confirmará.`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error canjeando"); } finally { setCanjeando(""); }
  };

  if (!club) return <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-blue-800" /></div>;
  const progreso = club.siguiente_nivel
    ? Math.min(100, Math.round((club.puntos_historicos / club.siguiente_nivel.puntos_min) * 100)) : 100;

  return (
    <div className="space-y-4" data-testid="mayorista-club-page">
      {/* Nivel y puntos */}
      <div className="rounded-2xl p-5 bg-gradient-to-br from-[#4A2C0A] to-[#6B4318] text-amber-50">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs text-amber-200/80">Club de la Abuela Negocios</p>
            <p className="font-['Playfair_Display'] text-xl">{club.nivel.emoji} Nivel {club.nivel.nombre}</p>
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold text-[#F5D78E]" data-testid="club-puntos">{club.puntos}</p>
            <p className="text-[10px] text-amber-200/80">puntos disponibles</p>
          </div>
        </div>
        {club.siguiente_nivel && (
          <div className="mt-3">
            <div className="h-2 bg-white/15 rounded-full overflow-hidden"><div className="h-full bg-[#C8902A]" style={{ width: `${progreso}%` }} /></div>
            <p className="text-[10px] text-amber-200/80 mt-1">{club.puntos_historicos}/{club.siguiente_nivel.puntos_min} pts históricos para ser {club.siguiente_nivel.emoji} {club.siguiente_nivel.nombre}</p>
          </div>
        )}
        <p className="text-[10px] text-amber-200/70 mt-2">{club.regla} · se acreditan al confirmarse el pago</p>
      </div>

      {/* Beneficios del nivel */}
      <div className="bg-white rounded-2xl border p-4">
        <p className="font-semibold text-[#4A2C0A] text-sm flex items-center gap-1 mb-2"><Crown className="w-4 h-4 text-[#C8902A]" /> Tus beneficios {club.nivel.nombre}</p>
        <ul className="text-xs text-gray-600 space-y-1">{club.nivel.beneficios.map((b) => <li key={b}>✓ {b}</li>)}</ul>
      </div>

      {/* Recompensas */}
      <div className="bg-white rounded-2xl border p-4 space-y-2">
        <p className="font-semibold text-[#4A2C0A] text-sm flex items-center gap-1"><Gift className="w-4 h-4 text-[#C8902A]" /> Canjea tus puntos</p>
        {club.recompensas.map((r) => (
          <div key={r.id} className="flex items-center justify-between border rounded-xl px-3 py-2" data-testid={`recompensa-${r.id}`}>
            <div><p className="text-sm font-medium text-[#4A2C0A]">{r.nombre}</p><p className="text-[11px] text-gray-500">{r.puntos} puntos</p></div>
            <Button size="sm" disabled={club.puntos < r.puntos || canjeando === r.id} onClick={() => canjear(r)}
              className="bg-[#C8902A] hover:bg-[#B07D1E]" data-testid={`canjear-btn-${r.id}`}>
              {canjeando === r.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Canjear"}
            </Button>
          </div>
        ))}
        {club.canjes.length > 0 && (
          <div className="pt-2 border-t">
            <p className="text-xs font-semibold text-gray-500 mb-1">Mis canjes</p>
            {club.canjes.map((c) => (
              <div key={c.id} className="flex justify-between items-center text-xs py-1">
                <span>{c.recompensa_nombre}</span>
                <span className={`px-2 py-0.5 rounded-full text-[10px] ${CANJE_BADGE[c.estado]}`}>{c.estado}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Reportes */}
      {reportes && (
        <div className="bg-white rounded-2xl border p-4 space-y-3" data-testid="mayorista-reportes-section">
          <p className="font-semibold text-[#4A2C0A] text-sm flex items-center gap-1"><TrendingUp className="w-4 h-4 text-[#C8902A]" /> Mis compras y reportes</p>
          <div className="grid grid-cols-2 gap-2 text-center">
            {[["Total compras", fmt(reportes.total_compras)], ["Pedidos", reportes.numero_pedidos],
              ["Ticket promedio", fmt(reportes.ticket_promedio)], ["Ahorro vs. precio público", fmt(reportes.ahorro_comercial)],
              ["Venta potencial", fmt(reportes.venta_potencial)], ["Margen potencial", fmt(reportes.margen_bruto_potencial)]].map(([l, v]) => (
              <div key={l} className="bg-[#FBF5E8] rounded-xl p-2.5"><p className="text-sm font-bold text-[#4A2C0A]">{v}</p><p className="text-[10px] text-gray-500">{l}</p></div>
            ))}
          </div>
          {reportes.compras_por_mes.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-gray-500 mb-1">Evolución mensual</p>
              {reportes.compras_por_mes.slice(-6).map((m) => (
                <div key={m.mes} className="flex justify-between text-xs py-0.5"><span>{m.mes}</span><span>{m.pedidos} pedidos · <b>{fmt(m.total)}</b></span></div>
              ))}
            </div>
          )}
          {reportes.productos_top.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-gray-500 mb-1">Productos más comprados</p>
              {reportes.productos_top.slice(0, 5).map((p) => (
                <div key={p.nombre} className="flex justify-between text-xs py-0.5"><span>{p.nombre}</span><span>{p.unidades} und</span></div>
              ))}
            </div>
          )}
          <p className="text-[10px] text-gray-400">{reportes.nota}</p>
        </div>
      )}
    </div>
  );
}
